import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { ErrorMessage, Validate, ValidateGroup } from '../../../../../components/Validation';
import Textbox from '../../../components/Textbox';
import Select from '../../../components/Select';
import OptionsContainer from '../../../components/OptionsContainer';
import PageSectionHeader from '../../../components/PageSectionHeader';
import messages from '../../messages';

/**
 * Renders a dynamic list of payment form fields based on configuration from the API.
 * Each field shape: { id, fieldName, fieldLabel, uiControl, tooltip, isCodeCheck, isEnableDefault, value, order, validateRule, dataSource }
 * validateRule shape: { isMandatory, isOnlyDigits, maxLength, minLength, complexRules }
 * dataSource (dropdown options) shape: array of { value, text } | { value, label } | { id, name } | { code, description } | string
 */
class DynamicPaymentFormFields extends React.Component {
  getDropdownOptions(field) {
    let raw = field.dataSource;
    // dataSource may arrive as a JSON string from the API
    if (typeof raw === 'string') {
      try {
        raw = JSON.parse(raw);
      } catch (e) {
        raw = [];
      }
    }
    if (!Array.isArray(raw)) return [];
    return raw
      .map((item) => {
        if (item === null || item === undefined) return null;
        if (typeof item === 'string' || typeof item === 'number') {
          return { value: String(item), label: String(item) };
        }
        const value = item.value !== undefined ? item.value
          : item.id !== undefined ? item.id
          : item.code !== undefined ? item.code
          : item.key;
        const label = item.text !== undefined ? item.text
          : item.label !== undefined ? item.label
          : item.name !== undefined ? item.name
          : item.description !== undefined ? item.description
          : value;
        if (value === undefined) return null;
        return { value: String(value), label: String(label) };
      })
      .filter(Boolean);
  }

  buildValidators(field) {
    const validators = [];
    const { validateRule } = field;
    if (!validateRule) return validators;

    if (validateRule.isMandatory) {
      const v = function (value) {
        if (value === undefined || value === null || value === '' || value === false) {
          return false;
        }
        return true;
      };
      v.messageKey = 'isRequired';
      validators.push(v);
    }

    if (validateRule.isOnlyDigits) {
      const v = function (value) {
        if (!value) return true; // empty is handled by isRequired
        return /^\d+$/.test(String(value));
      };
      v.messageKey = 'isOnlyDigits';
      validators.push(v);
    }

    if (validateRule.complexRules && validateRule.complexRules.length) {
      validateRule.complexRules.forEach((complexRule, index) => {
        const pattern = complexRule.regexPattern;
        const v = function (value) {
          if (!value) return true; // empty is handled by isRequired
          if (!pattern) return true;
          return new RegExp(pattern).test(String(value));
        };
        v.messageKey = `complexRule${index}`;
        v.errorMessage = complexRule.errorMessage;
        validators.push(v);
      });
    }

    if (validateRule.minLength) {
      const min = validateRule.minLength;
      const v = function (value) {
        if (!value) return true; // empty is handled by isRequired
        return String(value).length >= min;
      };
      v.messageKey = 'isMinLength';
      validators.push(v);
    }

    if (validateRule.maxLength) {
      const max = validateRule.maxLength;
      const v = function (value) {
        if (!value) return true;
        return String(value || '').length <= max;
      };
      v.messageKey = 'isMaxLength';
      validators.push(v);
    }

    return validators;
  }

  renderTextField(field, value, isReadOnly, validatorGroup, validationCounter) {
    const { onChange, onCodeFieldBlur, codeVerificationFailed, fields } = this.props;
    const validators = this.buildValidators(field);
    const { validateRule } = field;

    // Trigger code lookup on blur/change only for:
    // 1. field.isCodeCheck is explicitly true, OR
    // 2. BankBranchCode acting as the primary transit/routing code (no SWIFT/IBAN present).
    // When SWIFT/IBAN is present (e.g. Japan) BankBranchCode is a plain optional field, not a lookup.
    const hasPrimaryCodeField = (fields || []).some((f) => f.fieldName === 'SwiftCode' || f.fieldName === 'IBAN');
    const isLookupField = field.isCodeCheck === true || (field.fieldName === 'BankBranchCode' && !hasPrimaryCodeField);
    const isExactLength = validateRule && validateRule.minLength && validateRule.maxLength && validateRule.minLength === validateRule.maxLength;

    const handleBlur = isLookupField && onCodeFieldBlur
      ? (e) => {
          const v = e.target.value;
          // Only trigger lookup when value meets the minimum required length
          if (v && (!validateRule || !validateRule.minLength || v.length >= validateRule.minLength)) {
            onCodeFieldBlur(field.fieldName, v);
          }
        }
      : undefined;

    // Also trigger immediately when the value reaches the required fixed length (on change)
    const handleChange = (e) => {
      const v = e.target.value;
      onChange(field.fieldName, v);
      if (isLookupField && onCodeFieldBlur && validateRule && validateRule.minLength > 0 && v.length === validateRule.minLength) {
        onCodeFieldBlur(field.fieldName, v);
      }
    };

    // Code-check fields (e.g. Routing Number) span the full width; other fields pair up two per row
    const columnClass = isLookupField ? 'col-md-12' : 'col-md-6';
    // Find the IsInvalidBsb field to get its label for the inline verification error
    const invalidBsbField = isLookupField && fields ? fields.find((f) => f.fieldName === 'IsInvalidBsb') : null;
    const isSwiftField = field.fieldName === 'SwiftCode';
    const verificationErrorLabel = isSwiftField
      ? <span>The <strong>SWIFT</strong> you have entered could not be verified.</span>
      : invalidBsbField ? invalidBsbField.fieldLabel : 'Routing Number cannot be verified';

    return (
      <div className={`${columnClass} form-group mb02`} key={field.id}>
        {validators.length > 0 ? (
          <Validate
            name={field.fieldName}
            group={validatorGroup}
            validators={validators}
            addValidatorToGroup={validatorGroup.addValidatorToGroup}
            validChangeInGroup={validatorGroup.validChangeInGroup}
            validationCounter={validationCounter}
            validatorLeaveGroup={validatorGroup.removeValidatorToGroup}
            fieldName={{ id: field.fieldName, defaultMessage: field.fieldLabel }}
            tooltipContent={field.tooltip || null}
            showAsterisk={!!(validateRule && validateRule.isMandatory)}
          >
            <Textbox
              disabled={isReadOnly}
              name={field.fieldName}
              type={validateRule && validateRule.isOnlyDigits ? 'number' : 'text'}
              onChange={handleChange}
              onBlur={handleBlur}
              value={value || ''}
            />
            {validators.map((validator) => {
              let msgContent;
              // Use "digits" only when the field accepts digits exclusively; otherwise "characters".
              const lengthUnit = validateRule && validateRule.isOnlyDigits ? 'digits' : 'characters';
              switch (validator.messageKey) {
                case 'isRequired':
                  msgContent = <span style={{ fontSize: '0.8em' }}>{field.fieldLabel} is required.</span>;
                  break;
                case 'isMinLength':
                  msgContent = isExactLength
                    ? <span style={{ fontSize: '0.8em' }}>{field.fieldLabel} must be exactly {validateRule.minLength} {lengthUnit}.</span>
                    : <span style={{ fontSize: '0.8em' }}>{field.fieldLabel} must contain at least {validateRule.minLength} {lengthUnit}.</span>;
                  break;
                case 'isMaxLength':
                  msgContent = isExactLength
                    ? <span style={{ fontSize: '0.8em' }}>{field.fieldLabel} must be exactly {validateRule.maxLength} {lengthUnit}.</span>
                    : <span style={{ fontSize: '0.8em' }}>{field.fieldLabel} must contain no more than {validateRule.maxLength} {lengthUnit}.</span>;
                  break;
                case 'isOnlyDigits':
                  msgContent = <span style={{ fontSize: '0.8em' }}><FormattedMessage {...messages.isOnlyDigits} values={{ defaultMessage: field.fieldLabel }} /></span>;
                  break;
                default:
                  msgContent = <span style={{ fontSize: '0.8em' }}>{validator.errorMessage || field.fieldLabel}</span>;
              }
              return (
                <ErrorMessage key={validator.messageKey} for={validator.messageKey}>
                  {msgContent}
                </ErrorMessage>
              );
            })}
          </Validate>
        ) : (
          <div>
            <PageSectionHeader>{field.fieldLabel}</PageSectionHeader>
            <Textbox
              disabled={isReadOnly}
              name={field.fieldName}
              type="text"
              onChange={handleChange}
              onBlur={handleBlur}
              value={value || ''}
            />
          </div>
        )}
        {field.tooltip && (
          <p style={{ fontSize: '0.8em', color: '#555', marginTop: 2 }}>
            {field.tooltip}
          </p>
        )}
        {isLookupField && codeVerificationFailed && (
          <p style={{ fontSize: '0.7em', color: 'red', marginTop: 2 }}>
            {verificationErrorLabel}
          </p>
        )}
      </div>
    );
  }

  renderDropdownField(field, value, isReadOnly, validatorGroup, validationCounter) {
    const { onChange } = this.props;
    const { validateRule } = field;
    const validators = this.buildValidators(field);
    const options = this.getDropdownOptions(field);

    const selectEl = (
      <Select
        disabled={isReadOnly}
        name={field.fieldName}
        value={value === undefined || value === null ? '' : value}
        onChange={(e) => onChange(field.fieldName, e.target.value)}
      >
        <option value="">{field.tooltip || `Select ${field.fieldLabel}`}</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </Select>
    );

    return (
      <div className={'col-md-6 form-group mb02'} key={field.id}>
        {validators.length > 0 ? (
          <Validate
            name={field.fieldName}
            group={validatorGroup}
            validators={validators}
            addValidatorToGroup={validatorGroup.addValidatorToGroup}
            validChangeInGroup={validatorGroup.validChangeInGroup}
            validationCounter={validationCounter}
            validatorLeaveGroup={validatorGroup.removeValidatorToGroup}
            fieldName={{ id: field.fieldName, defaultMessage: field.fieldLabel }}
            tooltipContent={field.tooltip || null}
            showAsterisk={!!(validateRule && validateRule.isMandatory)}
          >
            {selectEl}
            <ErrorMessage for="isRequired">
              <span style={{ fontSize: '0.8em' }}>{field.fieldLabel} is required.</span>
            </ErrorMessage>
          </Validate>
        ) : (
          <div>
            <PageSectionHeader>{field.fieldLabel}</PageSectionHeader>
            {selectEl}
          </div>
        )}
      </div>
    );
  }

  renderCheckboxField(field, value, isReadOnly) {
    const { onChange } = this.props;
    return (
      <div className={'col-md-12 form-group mb02'} key={field.id}>
        <OptionsContainer style={{ marginBottom: 0 }}>
          <input
            disabled={isReadOnly}
            type="checkbox"
            name={field.fieldName}
            checked={!!value}
            onChange={(e) => onChange(field.fieldName, e.target.checked)}
          />
          <span>{field.fieldLabel}</span>
        </OptionsContainer>
      </div>
    );
  }

  render() {
    const { fields, formValues, isReadOnly, validatorGroup, validationCounter } = this.props;

    if (!fields || fields.length === 0) {
      return null;
    }

    // 'Button' fields (e.g. a legacy manual "Validate" trigger) have no React rendering here - drop them.
    const sortedFields = [...fields]
      .filter((field) => (field.uiControl || '').toLowerCase() !== 'button')
      .sort((a, b) => a.order - b.order);
    const isInvalidBsbChecked = !!formValues['IsInvalidBsb'];

    // Share one Bootstrap row so col-md-6 fields sit two-per-line instead of one-per-line
    return (
      <div className="row">
        {sortedFields.map((field) => {
          const value = formValues[field.fieldName];
          // IsInvalidBsb checkbox: always interactive
          // Code-check fields (SWIFT/Transit/Routing): enabled when IsInvalidBsb is NOT checked
          // Fields flagged isEnableDefault by the backend are always editable
          // Remaining fields: enabled only when IsInvalidBsb IS checked
          const isCheckboxControl = field.fieldName === 'IsInvalidBsb';
          const isCodeCheckField = field.isCodeCheck === true; // only API-flagged fields lock when invalid is accepted
          const isBranchCodeField = field.fieldName === 'BankBranchCode';
          let fieldDisabled;
          if (isCheckboxControl) {
            fieldDisabled = isReadOnly;
          } else if (isCodeCheckField) {
            fieldDisabled = isReadOnly || isInvalidBsbChecked;
          } else if (isBranchCodeField || field.isEnableDefault) {
            fieldDisabled = isReadOnly;
          } else {
            fieldDisabled = isReadOnly || !isInvalidBsbChecked;
          }

          if (field.uiControl === 'checkbox') {
            return this.renderCheckboxField(field, value, fieldDisabled);
          }
          if ((field.uiControl || '').toLowerCase() === 'dropdown') {
            return this.renderDropdownField(field, value, fieldDisabled, validatorGroup, validationCounter);
          }
          return this.renderTextField(field, value, fieldDisabled, validatorGroup, validationCounter);
        })}
      </div>
    );
  }
}

DynamicPaymentFormFields.propTypes = {
  fields: PropTypes.array,
  formValues: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onCodeFieldBlur: PropTypes.func,
  codeVerificationFailed: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  validatorGroup: PropTypes.object.isRequired,
  validationCounter: PropTypes.number,
};

DynamicPaymentFormFields.defaultProps = {
  fields: [],
  formValues: {},
  codeVerificationFailed: false,
  isReadOnly: false,
  validationCounter: 0,
};

export default DynamicPaymentFormFields;



// WEBPACK FOOTER //
// ./app/containers/FastCover/Appsure.StepBankDetails/components/DynamicPaymentFormFields/index.js